/*******************************************************************************
 * Copyright (c) 2024-2026 Malond Limited. All rights reserved.
 * The source code is the property of Malond Limited.
 ******************************************************************************/

import { useCallback } from "react"
import { getDefaultStore, useAtom, useAtomValue } from "jotai"
import { useNavigate } from "react-router-dom"
import { mutate } from "swr"
import {
  sessionTokenAtom,
  userProfileAtom,
  activeShopAtom,
  authLoadingAtom,
  isAuthenticatedAtom,
  userRoleAtom,
  userNameAtom,
  userShopsAtom,
} from "../store/auth"
import { AuthService } from "../services/authService"
import { authStorageAdapter } from "../libs/auth/storageAdapter"
import { AuthUserProfile, UserShop } from "../libs/kratos/types"
import { API } from "../libs/api/axios"
import { getBasePath } from "../libs/api/url"
import { KRATOS_WHOAMI_KEY, buildKratosSessionResult } from "./useUser"

let inFlightCheckAuthPromise: Promise<boolean> | null = null

export function resetInFlightCheckAuthPromise(): void {
  inFlightCheckAuthPromise = null
}

export function buildUserProfileFromSession(
  session: any
): AuthUserProfile | null {
  const userId = session?.identity?.id
  const traits = session?.identity?.traits

  if (!userId) {
    return null
  }

  const userRole = traits?.role || "seller"
  return {
    id: userId,
    email: traits?.email || "",
    username: traits?.username || traits?.email || "",
    role: userRole,
    shopIds: [],
    shops: [],
  }
}

export async function fetchEnterprises(): Promise<any[]> {
  try {
    const basePath = getBasePath()
    const enterpriseRes = await API.get(`${basePath}/enterprise`)
    return enterpriseRes?.data?.data || []
  } catch (err) {
    console.warn("[useAuth]: Не удалось получить список предприятий:", err)
    return []
  }
}

export async function navigateAfterLoginLogic(
  enterprises: any[],
  profile: AuthUserProfile,
  navigate: (path: string) => void,
  setActiveShop: (shop: UserShop) => void
): Promise<void> {
  // Если предприятий нет или их больше одного — переход к экрану выбора предприятия /e
  if (!enterprises || enterprises.length !== 1) {
    navigate("/e")
    return
  }

  // Ровно 1 предприятие:
  const singleEnterprise = enterprises[0]
  const targetEnterpriseId = singleEnterprise.id

  try {
    const basePath = getBasePath()
    const shopRes = await API.get(
      `${basePath}/enterprise/${targetEnterpriseId}/shop`
    )
    const shops = shopRes?.data?.data || []

    // Ровно 1 магазин:
    if (shops.length === 1) {
      const singleShop = shops[0]
      setActiveShop(singleShop)

      const salePointRes = await API.get(
        `${basePath}/enterprise/${targetEnterpriseId}/shopSalePoint?shopId=${singleShop.id}`
      )
      const salePoints = salePointRes?.data?.data || []

      if (salePoints.length === 1) {
        // Ровно 1 касса: открываем интерфейс продаж!
        navigate(
          `/e/${targetEnterpriseId}/${singleShop.id}/${salePoints[0].id}/sales`
        )
        return
      } else if (salePoints.length > 1) {
        // Несколько касс: переход к выбору конкретной кассы
        navigate(`/e/${targetEnterpriseId}/${singleShop.id}`)
        return
      }
    }
  } catch (err) {
    console.warn(
      "[useAuth]: Не удалось проверить структуру единственного предприятия:",
      err
    )
  }

  // По умолчанию переходим к выбору магазина предприятия
  navigate(`/e/${targetEnterpriseId}`)
}

export async function performCheckAuth(
  autoNavigate = false,
  options?: {
    store?: ReturnType<typeof getDefaultStore>
    navigate?: (path: string) => void
  }
): Promise<boolean> {
  const store = options?.store || getDefaultStore()
  const navigate = options?.navigate

  if (inFlightCheckAuthPromise) {
    const isAuth = await inFlightCheckAuthPromise
    if (autoNavigate && isAuth && navigate) {
      const profile = store.get(userProfileAtom)
      const enterprises = await fetchEnterprises()
      if (profile) {
        await navigateAfterLoginLogic(
          enterprises,
          profile,
          navigate,
          (shop) => {
            store.set(activeShopAtom, shop)
          }
        )
      } else {
        navigate("/e")
      }
    }
    return isAuth
  }

  store.set(authLoadingAtom, true)
  const promise = (async () => {
    try {
      const session = await AuthService.checkSession()
      if (session && session.active) {
        // Восстанавливаем токен сессии
        const storedToken = await authStorageAdapter.getToken()
        const effectiveToken = storedToken || session.id
        const profile = buildUserProfileFromSession(session)

        store.set(sessionTokenAtom, effectiveToken)
        store.set(userProfileAtom, profile)

        const sessionResult = buildKratosSessionResult(session, effectiveToken)
        mutate(KRATOS_WHOAMI_KEY, sessionResult, false)

        if (autoNavigate && navigate) {
          const enterprises = await fetchEnterprises()
          if (profile) {
            await navigateAfterLoginLogic(
              enterprises,
              profile,
              navigate,
              (shop) => {
                store.set(activeShopAtom, shop)
              }
            )
          } else {
            navigate("/e")
          }
        }
        return true
      } else {
        store.set(sessionTokenAtom, null)
        store.set(userProfileAtom, null)
        store.set(activeShopAtom, null)
        mutate(KRATOS_WHOAMI_KEY, buildKratosSessionResult(null), false)
        return false
      }
    } catch (err) {
      console.error("[useAuth]: Ошибка при проверке сессии:", err)
      store.set(sessionTokenAtom, null)
      store.set(userProfileAtom, null)
      store.set(activeShopAtom, null)
      mutate(KRATOS_WHOAMI_KEY, buildKratosSessionResult(null), false)
      return false
    } finally {
      store.set(authLoadingAtom, false)
      inFlightCheckAuthPromise = null
    }
  })()

  inFlightCheckAuthPromise = promise
  return promise
}

export function useAuth() {
  const [sessionToken, setSessionToken] = useAtom(sessionTokenAtom)
  const [userProfile, setUserProfile] = useAtom(userProfileAtom)
  const [activeShop, setActiveShop] = useAtom(activeShopAtom)
  const [isLoading, setIsLoading] = useAtom(authLoadingAtom)

  const isAuthenticated = useAtomValue(isAuthenticatedAtom)
  const role = useAtomValue(userRoleAtom)
  const userName = useAtomValue(userNameAtom)
  const shops = useAtomValue(userShopsAtom)

  const navigate = useNavigate()

  /**
   * Загружает профиль пользователя и предприятия с бэкенда Sales API.
   */
  const loadUserProfile = useCallback(
    async (
      session: any
    ): Promise<{ profile: AuthUserProfile; enterprises: any[] } | null> => {
      try {
        const profile = buildUserProfileFromSession(session)
        if (!profile) {
          throw new Error("ID пользователя не найден в сессии Kratos")
        }

        setUserProfile(profile)
        const enterprises = await fetchEnterprises()

        return { profile, enterprises }
      } catch (err) {
        console.error(
          "[useAuth]: Ошибка при загрузке профиля пользователя:",
          err
        )
        return null
      }
    },
    [setUserProfile]
  )

  /**
   * Алгоритм навигации кассира после входа:
   * - Если предприятий нет или их больше одного -> выбор предприятия (/e).
   * - Если предприятие ровно 1:
   *   - Если касса ровно 1 -> переход в продажи (/e/{enterpriseId}/{shopId}/{shopSalePointId}/sales).
   *   - Если магазинов несколько -> выбор магазина (/e/{enterpriseId}).
   */
  const navigateAfterLogin = useCallback(
    async (enterprises: any[], profile: AuthUserProfile) => {
      await navigateAfterLoginLogic(
        enterprises,
        profile,
        navigate,
        setActiveShop
      )
    },
    [navigate, setActiveShop]
  )

  /**
   * Выполняет аутентификацию через Kratos и последующий вход в систему.
   */
  const login = useCallback(
    async (identifier: string, pass: string): Promise<boolean> => {
      setIsLoading(true)
      try {
        const result = await AuthService.login(identifier, pass)
        const profile = buildUserProfileFromSession(result.session)

        setSessionToken(result.sessionToken)
        setUserProfile(profile)

        // Обновляем SWR кэш для whoami без повторного сетевого запроса
        const sessionResult = buildKratosSessionResult(
          result.session,
          result.sessionToken
        )
        mutate(KRATOS_WHOAMI_KEY, sessionResult, false)

        const enterprises = await fetchEnterprises()
        if (profile) {
          await navigateAfterLogin(enterprises, profile)
        } else {
          // Если профиль не удалось загрузить, направляем на выбор предприятия
          navigate("/e")
        }
        return true
      } finally {
        setIsLoading(false)
      }
    },
    [navigateAfterLogin, setIsLoading, setSessionToken, setUserProfile]
  )

  /**
   * Проверяет существующую сессию при старте приложения или перезагрузке (Ctrl+R).
   * При autoNavigate === false (восстановление сессии без редиректа) запрос /enterprise не выполняется.
   */
  const checkAuth = useCallback(
    async (autoNavigate = false): Promise<boolean> => {
      return performCheckAuth(autoNavigate, {
        store: getDefaultStore(),
        navigate,
      })
    },
    [navigate]
  )

  /**
   * Выбор магазина из списка доступных.
   */
  const selectShop = useCallback(
    async (shop: UserShop) => {
      setActiveShop(shop)

      // Проверяем точки продаж выбранного магазина
      try {
        const basePath = getBasePath()
        const salePointRes = await API.get(
          `${basePath}/enterprise/${shop.enterpriseId}/shopSalePoint?shopId=${shop.id}`
        )
        const salePoints = salePointRes?.data?.data || []

        if (salePoints.length === 1) {
          navigate(
            `/e/${shop.enterpriseId}/${shop.id}/${salePoints[0].id}/sales`
          )
          return
        }
      } catch (err) {
        console.warn("[useAuth]: Ошибка при проверке точек продаж:", err)
      }

      navigate(`/e/${shop.enterpriseId}/${shop.id}`)
    },
    [navigate, setActiveShop]
  )

  /**
   * Завершение сессии (Logout).
   */
  const logout = useCallback(async () => {
    setIsLoading(true)
    try {
      await AuthService.logout()
    } finally {
      resetInFlightCheckAuthPromise()
      setSessionToken(null)
      setUserProfile(null)
      setActiveShop(null)
      setIsLoading(false)
      mutate(KRATOS_WHOAMI_KEY, buildKratosSessionResult(null), false)
      navigate("/login")
    }
  }, [navigate, setActiveShop, setIsLoading, setSessionToken, setUserProfile])

  return {
    sessionToken,
    userProfile,
    activeShop,
    isLoading,
    isAuthenticated,
    role,
    userName,
    shops,
    login,
    logout,
    checkAuth,
    selectShop,
    navigateAfterLogin,
  }
}
